'use client';
import { cn } from '@woonstad/helpers-utils';
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { SITES, SitesType } from '../../types/SitesType';
import { Typography } from '../typography/Typography';
import { SvgSWTriangleBar } from './sw-triangle-bar';

function useIntersectionAnimation(
  threshold: number,
  onVisible: () => void
): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onVisible();
            observer.disconnect();
          }
        });
      },
      { threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, onVisible]);

  return ref;
}

type AnimatedBallProps = {
  state: 'initial' | 'visible' | 'settled';
  className: string;
  settledAnimation: string;
  visibleAnimation?: string;
};

const AnimatedBall = ({
  state,
  className,
  settledAnimation,
  visibleAnimation = 'animate-in fade-in zoom-in',
}: AnimatedBallProps) => (
  <div
    className={cn(
      'absolute rounded-full',
      {
        'scale-0': state === 'initial',
        [visibleAnimation]: state === 'visible',
        [settledAnimation]: state === 'settled',
      },
      className
    )}
  />
);

type Props = {
  quote?: string;
  bron?: string;
  site: SitesType;
}

/**
 * Goal
 *
 * Quote is a content component to emphasize a short piece of text.
 *
 */
export const Quote = ({
  quote,
  bron,
  site,
}: Props) => {

  const classes = cn('relative max-h-[25rem] max-w-prose bg-quote', {
    'after:absolute after:-right-7 after:-top-7 after:size-14 after:rotate-[45deg] border-transparent after:bg-[white]': site === SITES.Stadswonen,
  });

  return (
    <blockquote className={classes}>
      {site === SITES.Woonstad ? (
        <WSBalls />
      ) : null}
      <div className="relative z-10 overflow-hidden p-14">
        {site === SITES.Stadswonen ? (
          <SvgSWTriangleBar className="absolute left-0 top-0" />
        ) : null}
        {quote ? (
          <Typography
            variant="title-body"
            className="mb-8 text-quote-title"
            type="p"
          >
            {quote}
          </Typography>
        ) : null}

        {bron ? (
          <Typography
            variant="label-regular"
            className=" text-quote-source"
            type="p"
          >
            {bron}
          </Typography>
        ) : null}
      </div>
    </blockquote >
  );
};

const WSBalls = () => {
  const [state, setState] = useState<'initial' | 'visible' | 'settled'>('initial');
  const onVisible = useCallback(() => {
    setState('visible');
    setTimeout(() => setState('settled'), 500);
  }, []);
  const ref = useIntersectionAnimation(0.6, onVisible);

  return (
    <div
      ref={ref}
      className="absolute z-10 flex size-full origin-top items-center justify-center"
    >
      <AnimatedBall
        state={state}
        className="bottom-2 left-10 size-[5.3rem] bg-[#ECF2FC]"
        settledAnimation="animate-[animate-ball_12s_infinite]"
        visibleAnimation="animate-in zoom-in duration-500 ease-out"
      />
      <AnimatedBall
        state={state}
        className="left-0 top-10 size-16 bg-[#FF7842] ease-out fill-mode-both max-sm:hidden"
        settledAnimation="animate-[animate-ball_13s_infinite]"
      />
      <AnimatedBall
        state={state}
        className="bottom-[5rem] right-[5rem] size-8 bg-[#F170B8] ease-out fill-mode-both md:right-[5rem] max-sm:top-2 max-sm:bottom-auto max-sm:right-2"
        settledAnimation="animate-[animate-ball_10s_infinite_reverse]"
      />
      <AnimatedBall
        state={state}
        className="right-[15rem] top-4 size-8 bg-[#00C5F2] ease-out fill-mode-both max-sm:hidden"
        settledAnimation="animate-[animate-ball_11s_infinite_reverse]"
      />
    </div>
  );
};
